// Tutorial hints (M5, DESIGN.md §14): a sequential goal checklist for new
// players — one hint card at a time (bottom-right), advancing when the game
// state shows the step is done. Skippable per-step; can be disabled entirely
// (persisted). Pure render-side: reads state, never touches the sim.

const HINTS_KEY = 'portage.hints';

/** state snapshot the tutorial evaluates against (built by main.ts) */
export interface TutorialCtx {
  /** player building counts by def id (any state incl. blueprint) */
  counts: Map<string, number>;
  /** player military unit count (soldiers, not scouts) */
  soldiers: number;
  camMoved: boolean;
  roadBuilt: boolean; // player issued a buildRoad command
  claimed: boolean; // player issued a claimHex command
  surveyed: boolean; // player issued a surveyHex command
  won: boolean;
}

interface Step {
  title: string;
  body: string;
  done(ctx: TutorialCtx): boolean;
}

const any = (ctx: TutorialCtx, ids: string[]): boolean =>
  ids.some((id) => (ctx.counts.get(id) ?? 0) > 0);

const STEPS: Step[] = [
  {
    title: 'Look around',
    body: 'Pan with WASD or hold the middle mouse button. Zoom with the wheel, rotate with Q/E. Your headquarters holds your starting wares.',
    done: (c) => c.camMoved,
  },
  {
    title: 'Chop wood',
    body: 'Build a Woodcutter near trees (bottom bar → wood). Buildings need materials delivered, then a surveyor constructs them.',
    done: (c) => any(c, ['woodcutter']),
  },
  {
    title: 'Lay a road',
    body: 'Drag a Road from your headquarters to the woodcutter. Porters carry every ware on foot — roads make them 50% faster.',
    done: (c) => c.roadBuilt,
  },
  {
    title: 'Saw planks',
    body: 'Build a Sawmill and connect it. It turns logs into planks — the backbone of all construction.',
    done: (c) => any(c, ['sawmill']),
  },
  {
    title: 'Claim land',
    body: 'Use Claim and click a hex next to your territory. A surveyor walks its border for 30s to claim it. You can only build on your own land.',
    done: (c) => c.claimed,
  },
  {
    title: 'Survey for deposits',
    body: 'Use Survey on a claimed hex (20s) to reveal what lies beneath — stone, coal, iron or gold. Mines can only be placed on surveyed deposits.',
    done: (c) => c.surveyed,
  },
  {
    title: 'Quarry stone',
    body: 'Build a Quarry near surface boulders. Stone joins planks in most construction costs.',
    done: (c) => any(c, ['quarry']),
  },
  {
    title: 'House your people',
    body: 'Build a Residence to raise your population cap — every worker, porter and soldier needs housing.',
    done: (c) => any(c, ['residence']),
  },
  {
    title: 'Feed the mines',
    body: 'Set up food: Well + Farm + Windmill + Bakery, a Fishery on a shore, or a Hunter near woods. Fed industry buildings work twice as fast.',
    done: (c) => any(c, ['well', 'farm', 'windmill', 'bakery', 'fishery', 'hunter', 'ranch']),
  },
  {
    title: 'Forge tools',
    body: 'Place Coal and Iron mines on surveyed deposits, then a Smelter and a Toolmaker. Every new worker consumes one tool.',
    done: (c) => any(c, ['toolmaker']),
  },
  {
    title: 'Raise an army',
    body: 'Build a Bowyer (bows cost only planks) and a Training Ground, then queue archers. One weapon + one villager = one soldier.',
    done: (c) => any(c, ['training_ground']) && c.soldiers > 0,
  },
  {
    title: 'Guard your borders',
    body: 'Build a Watchtower near the frontier and garrison soldiers in it (RMB the tower). Towers anchor your territory — lose them and hexes flip.',
    done: (c) => any(c, ['watchtower']),
  },
  {
    title: 'Destroy the enemy',
    body: 'Scout the map, then march your army into the enemy base (RMB = attack-move) and raze their headquarters to win. Good luck!',
    done: (c) => c.won,
  },
];

export class Tutorial {
  private el: HTMLElement;
  private step = 0;
  private enabled: boolean;
  private lastKey = '';
  private doneShownAt = 0;

  constructor(parent: HTMLElement) {
    this.enabled = localStorage.getItem(HINTS_KEY) !== 'off';
    this.el = document.createElement('div');
    this.el.id = 'tutorial';
    this.el.className = 'panel';
    this.el.style.display = 'none';
    parent.appendChild(this.el);
    this.el.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      if (t.id === 'tut-skip') this.advance();
      else if (t.id === 'tut-off') this.setEnabled(false);
    });
  }

  get isEnabled(): boolean {
    return this.enabled;
  }

  setEnabled(on: boolean): void {
    this.enabled = on;
    localStorage.setItem(HINTS_KEY, on ? 'on' : 'off');
    if (!on) {
      this.el.style.display = 'none';
      this.lastKey = '';
    }
  }

  private advance(): void {
    this.step++;
    this.doneShownAt = 0;
    this.lastKey = '';
  }

  /** call at a low cadence (~2 Hz) with fresh state */
  update(ctx: TutorialCtx): void {
    if (!this.enabled || this.step >= STEPS.length) {
      this.el.style.display = 'none';
      return;
    }
    const s = STEPS[this.step];
    const done = s.done(ctx);
    const now = performance.now();
    if (done && this.doneShownAt === 0) this.doneShownAt = now;
    if (this.doneShownAt > 0 && now - this.doneShownAt > 1600) {
      this.advance();
      return;
    }
    const key = `${this.step}:${done}`;
    if (key === this.lastKey) return;
    this.lastKey = key;
    this.el.style.display = 'block';
    this.el.innerHTML = `
      <div class="row"><b>${done ? '✓ ' : ''}${s.title}</b>
        <span class="dim" style="margin-left:auto">hint ${this.step + 1}/${STEPS.length}</span></div>
      <div class="tut-body ${done ? 'tut-done' : ''}">${s.body}</div>
      <div class="row" style="margin-top:6px;margin-bottom:0">
        <button id="tut-skip">skip</button>
        <button id="tut-off" class="dim-btn">hide hints</button>
      </div>`;
  }
}
